import React, { useState } from "react";
import { Table, Box, SpaceBetween, Header, Container, ColumnLayout, Badge, Button } from "@cloudscape-design/components";
import { useTranslation } from "react-i18next";

export default function LegacyWebhooksList({ webhooks, loading, onRefresh, onEdit }) {
  const { t } = useTranslation();
  const [sortingColumn, setSortingColumn] = useState({ sortingField: "webhook_id" });
  const [sortingDescending, setSortingDescending] = useState(false);

  // Calculate summary statistics
  const totalWebhooks = webhooks.length;
  const enabledWebhooks = webhooks.filter((w) => w.enabled).length;
  const totalNewsItems = webhooks.reduce((sum, w) => sum + w.news_items_count, 0);
  const webhooksWithFilters = webhooks.filter((w) => w.regions_count > 0 || w.services_count > 0 || w.keywords_count > 0).length;

  // Sort webhooks
  const sortedWebhooks = [...webhooks].sort((a, b) => {
    const field = sortingColumn.sortingField || "webhook_id";
    let aValue = a[field];
    let bValue = b[field];

    // Handle different data types
    if (typeof aValue === "string") {
      const comparison = aValue.localeCompare(bValue, undefined, {
        numeric: true,
        sensitivity: "base",
      });
      return sortingDescending ? -comparison : comparison;
    } else if (typeof aValue === "number") {
      const comparison = aValue - bValue;
      return sortingDescending ? -comparison : comparison;
    } else if (typeof aValue === "boolean") {
      const comparison = aValue === bValue ? 0 : aValue ? 1 : -1;
      return sortingDescending ? -comparison : comparison;
    }
    return 0;
  });

  return (
    <SpaceBetween size="l">
      {/* Summary Box */}
      <Container header={<Header variant="h3">{t("admin.legacy.summary.title")}</Header>}>
        <ColumnLayout columns={4} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">{t("admin.legacy.summary.total-webhooks")}</Box>
            <Box variant="awsui-value-large">{totalWebhooks}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">{t("admin.legacy.summary.enabled-webhooks")}</Box>
            <Box variant="awsui-value-large">{enabledWebhooks}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">{t("admin.legacy.summary.with-filters")}</Box>
            <Box variant="awsui-value-large">{webhooksWithFilters}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">{t("admin.legacy.summary.news-items-sent")}</Box>
            <Box variant="awsui-value-large">{totalNewsItems}</Box>
          </div>
        </ColumnLayout>
      </Container>

      {/* Webhooks Table */}
      <Table
        loading={loading}
        loadingText={t("admin.legacy.loading")}
        sortingColumn={sortingColumn}
        sortingDescending={sortingDescending}
        onSortingChange={({ detail }) => {
          setSortingColumn(detail.sortingColumn);
          setSortingDescending(detail.isDescending);
        }}
        columnDefinitions={[
          {
            id: "webhook_id",
            header: t("admin.legacy.field.webhook-id"),
            cell: (item) => item.webhook_id || "-",
            sortingField: "webhook_id",
          },
          {
            id: "channel_name",
            header: t("admin.legacy.field.channel-name"),
            cell: (item) => item.channel_name || "-",
            sortingField: "channel_name",
          },
          {
            id: "enabled",
            header: t("admin.legacy.field.status"),
            cell: (item) =>
              item.enabled ? <Badge color="green">{t("admin.legacy.status.enabled")}</Badge> : <Badge color="red">{t("admin.legacy.status.disabled")}</Badge>,
            sortingField: "enabled",
          },
          {
            id: "filters",
            header: t("admin.legacy.field.filters"),
            cell: (item) => {
              const filters = [];
              if (item.regions_count > 0) {
                filters.push(`${item.regions_count} ${t("admin.legacy.filter.regions")}`);
              }
              if (item.services_count > 0) {
                filters.push(`${item.services_count} ${t("admin.legacy.filter.services")}`);
              }
              if (item.keywords_count > 0) {
                filters.push(`${item.keywords_count} ${t("admin.legacy.filter.keywords")}`);
              }
              return filters.length > 0 ? filters.join(", ") : t("admin.legacy.filter.none");
            },
          },
          {
            id: "content_types",
            header: t("admin.legacy.field.content-types"),
            cell: (item) => {
              const types = item.content_types || [];
              return types.length > 0 ? types.join(", ") : t("admin.legacy.content.all");
            },
          },
          {
            id: "news_items_count",
            header: t("admin.legacy.field.news-items-sent"),
            cell: (item) => item.news_items_count.toLocaleString(),
            sortingField: "news_items_count",
          },
          {
            id: "last_updated",
            header: t("admin.legacy.field.last-updated"),
            cell: (item) => (item.last_updated ? new Date(item.last_updated).toLocaleString() : "-"),
            sortingField: "last_updated",
          },
          {
            id: "actions",
            header: t("admin.legacy.field.actions"),
            cell: (item) => (
              <Button variant="inline-link" iconName="edit" onClick={() => onEdit && onEdit(item)}>
                {t("admin.action.edit")}
              </Button>
            ),
          },
        ]}
        items={sortedWebhooks}
        empty={
          <Box textAlign="center" color="inherit">
            <b>{t("admin.legacy.no-webhooks")}</b>
            <Box padding={{ bottom: "s" }} variant="p" color="inherit">
              {t("admin.legacy.no-webhooks-desc")}
            </Box>
          </Box>
        }
        header={
          <Header
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="refresh" onClick={onRefresh}>
                  {t("admin.action.refresh")}
                </Button>
              </SpaceBetween>
            }
          >
            {t("admin.legacy.title")}
          </Header>
        }
      />
    </SpaceBetween>
  );
}
